import React from 'react';
import { Form, useActionData, useSubmit } from 'react-router-dom';
import { ValidationError } from '@oysterjs/core/graphql';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import {
  Action,
  LoggedInAgencyUserFieldsFragment,
  Resource,
  SubscriptionEntitlement,
  UpdateVoiceAgentMutation,
  VoiceAgentConfigurationModelVoice,
  VoiceAgentFieldsFragment
} from '~/api/graphql';
import { PermissionNotice, usePermissions } from '~/components/permissions';

const UPDATE_VOICE_AGENT = [{ resource: Resource.VoiceAgent, action: Action.Update }];
import {
  pendingChangesSaveMessages,
  PendingChangesFormHeader,
  PendingChangesSaveBar,
  PendingChangesSaveToast,
  withPendingChanges
} from '~/components/settings/pending-changes-save';
import { useVoiceAgentPendingChangesSave } from './use-voice-agent-pending-changes-save';
import {
  VoiceAgentAgencyInformationSection,
  VoiceAgentBasicInformationSection,
  VoiceAgentCallBehaviorSection,
  VoiceAgentEmailNotificationsSection,
  VoiceAgentOutboundCallBehaviorSection,
  VoiceAgentVoiceModelSection
} from './voice-agent-sections';
import { getVoiceAgentAgencyPlaceholders } from './voice-agent-section-props';
import {
  voiceAgentFieldsToVoiceAgentInput,
  VoiceAgentSettingsFormInput
} from './voice_agent_form_input';
import { voiceOptions } from './voice-model-options';
import {
  filterLanguageOptions,
  filterVoiceOptions,
  voiceAgentLanguageOptions,
  type VoiceAgentLanguageSupportData
} from './voice_language_support';

export const OutboundVoiceAgentSettingsForm = (props: {
  initialVoiceAgent: VoiceAgentFieldsFragment;
  agencyUser: LoggedInAgencyUserFieldsFragment;
  languageSupport: VoiceAgentLanguageSupportData;
}) => {
  const action = useActionData() as ActionResult<UpdateVoiceAgentMutation>;
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_VOICE_AGENT);
  const submit = useSubmit();
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<VoiceAgentSettingsFormInput>(() =>
    voiceAgentFieldsToVoiceAgentInput(props.initialVoiceAgent)
  );

  const { updateForm: _updateForm, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  const { changesPending, saved, error, discardChanges, markPendingChanges } =
    useVoiceAgentPendingChangesSave({
      action,
      initialVoiceAgent: props.initialVoiceAgent,
      onAfterSave: setForm
    });

  const updateForm = withPendingChanges(markPendingChanges, _updateForm);

  React.useEffect(() => {
    setValidationErrors(action?.validationErrors || []);
  }, [action?.validationErrors]);

  const selectedLanguages = form.configuration.languages;
  const selectedVoice = form.configuration.model.voice ?? VoiceAgentConfigurationModelVoice.Grace;
  const filteredVoiceOptions = React.useMemo(
    () => filterVoiceOptions(props.languageSupport, voiceOptions, selectedLanguages, selectedVoice),
    [props.languageSupport, selectedLanguages, selectedVoice]
  );
  const filteredLanguageOptions = React.useMemo(
    () =>
      filterLanguageOptions(
        props.languageSupport,
        voiceAgentLanguageOptions,
        selectedLanguages,
        selectedVoice
      ),
    [props.languageSupport, selectedLanguages, selectedVoice]
  );

  const agencyPlaceholders = React.useMemo(
    () => getVoiceAgentAgencyPlaceholders(props.agencyUser),
    [props.agencyUser]
  );

  const hasAdvancedCustomization = props.agencyUser.agency?.entitlements.some(
    (entitlement) => entitlement === SubscriptionEntitlement.AdvancedVoiceCustomization
  );

  const getFieldId = (path: string[]): string => `field-${path.join('-')}`;

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors([]);
    submit({ input: form }, { method: 'PUT', encType: 'application/json' });
  };

  const sectionProps = {
    form,
    phoneNumber: props.initialVoiceAgent.phoneNumber,
    agencyPlaceholders,
    validationErrors,
    getFieldId,
    getValidationError,
    updateForm,
    filteredVoiceOptions,
    filteredLanguageOptions,
    languageSupport: props.languageSupport,
    hasAdvancedCustomization,
    isOutboundVoice: true,
    supportsMultipleLanguages: true,
    outboundIntroComplianceDisclosure: props.initialVoiceAgent.outboundIntroComplianceDisclosure
  };

  return (
    <Form onSubmit={handleSave} className="space-y-6">
      <PendingChangesSaveToast
        saved={saved}
        error={error}
        messages={pendingChangesSaveMessages.voiceAgent}
      />
      {canUpdate ? (
        <PendingChangesSaveBar
          changesPending={changesPending}
          onDiscard={() => discardChanges(setForm)}
        />
      ) : (
        <PermissionNotice>
          You can see how this voice agent is configured but not change it.
        </PermissionNotice>
      )}
      <PendingChangesFormHeader
        title={`${form.name} Agent Settings`}
        description="Configure the settings for your voice agent."
        backHref="/app/settings/voice"
        changesPending={changesPending}
      />

      <VoiceAgentBasicInformationSection {...sectionProps} />
      <VoiceAgentAgencyInformationSection {...sectionProps} />
      <VoiceAgentCallBehaviorSection {...sectionProps} />
      <VoiceAgentVoiceModelSection {...sectionProps} />
      <VoiceAgentEmailNotificationsSection {...sectionProps} />
      <VoiceAgentOutboundCallBehaviorSection {...sectionProps} />
    </Form>
  );
};
